// API configuration and client
const API_URL = import.meta.env.VITE_API_URL || '';

export interface Account {
  id: string;
  email: string;
  displayName: string;
  provider: string;
  isActive: boolean;
  createdAt: string;
}

export interface Email {
  id: string;
  accountId: string;
  messageId: string;
  threadId: string;
  from: string;
  to: string;
  cc: string;
  bcc: string;
  subject: string;
  body: string;
  bodyPreview: string;
  receivedAt: string;
  isRead: boolean;
  isStarred: boolean;
  category: string;
  urgency: string;
  actionRequired: boolean;
  isNewsletter: boolean;
  newsletterName: string;
  summary: string;
  actionabilityScore: number;
  suggestedAction: string;
  labels: string;
}

export interface ConversationMessage {
  id: string;
  conversationId: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
}

export interface Conversation {
  exists: boolean;
  conversationId?: string;
  emailId: string;
  messages: ConversationMessage[];
}

class ApiClient {
  private token: string | null = null;

  setToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem('authToken', token);
    } else {
      localStorage.removeItem('authToken');
    }
  }

  getToken(): string | null {
    if (!this.token) {
      this.token = localStorage.getItem('authToken');
    }
    return this.token;
  }

  private async fetch<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const token = this.getToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...((options.headers as Record<string, string>) || {}),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers,
    });

    if (response.status === 401) {
      this.setToken(null);
      window.location.href = '/login';
      throw new Error('Unauthorized');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(error.message || `HTTP error ${response.status}`);
    }

    return response.json();
  }

  // Auth endpoints
  async validateToken(): Promise<{ valid: boolean; accountId: string; email: string }> {
    return this.fetch('/api/auth/validate');
  }

  getGmailAuthUrl(): string {
    return `${API_URL}/api/auth/gmail/start`;
  }

  // Account endpoints
  async getAccounts(): Promise<{ accounts: Account[]; count: number }> {
    const response = await this.fetch<{ accounts: any[] }>('/api/accounts');
    // Map the response to Account format if needed
    const accounts = response.accounts.map((acc: any) => ({
      id: acc.ID,
      email: acc.Email,
      displayName: acc.Email.split('@')[0],
      provider: acc.Provider,
      isActive: acc.SyncStatus === 'active',
      createdAt: acc.CreatedAt,
    }));
    return { accounts, count: accounts.length };
  }

  // Email endpoints
  async getEmails(params: {
    accountId?: string;
    limit?: number;
    offset?: number;
    filter?: string;
    category?: string;
    search?: string;
  }): Promise<{
    emails: Email[];
    count: number;
    totalCount: number;
  }> {
    const searchParams = new URLSearchParams();
    if (params.accountId) searchParams.set('accountId', params.accountId);
    if (params.limit) searchParams.set('limit', params.limit.toString());
    if (params.offset) searchParams.set('offset', params.offset.toString());
    if (params.filter) searchParams.set('filter', params.filter);
    if (params.category) searchParams.set('category', params.category);
    if (params.search) searchParams.set('search', params.search);

    return this.fetch(`/api/emails?${searchParams}`);
  }

  async getEmail(id: string): Promise<{ email: Email }> {
    return this.fetch(`/api/emails/${id}`);
  }

  // Conversation endpoints
  async getConversation(emailId: string, accountId?: string): Promise<Conversation> {
    const searchParams = new URLSearchParams();
    searchParams.set('emailId', emailId);
    if (accountId) searchParams.set('accountId', accountId);
    return this.fetch(`/api/conversations/by-email?${searchParams}`);
  }

  async sendMessage(conversationId: string, message: string, emailId?: string): Promise<{
    success: boolean;
    conversationId: string;
    response: string;
  }> {
    return this.fetch(`/api/conversations/${conversationId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ message, emailId }),
    });
  }

  // Action endpoints
  async archiveEmail(emailId: string, accountId?: string): Promise<{ success: boolean }> {
    const searchParams = new URLSearchParams();
    searchParams.set('emailId', emailId);
    if (accountId) searchParams.set('accountId', accountId);
    return this.fetch(`/api/actions/archive?${searchParams}`, { method: 'POST' });
  }

  async tagEmail(emailId: string, tag: string, accountId?: string): Promise<{ success: boolean }> {
    const searchParams = new URLSearchParams();
    searchParams.set('emailId', emailId);
    searchParams.set('tag', tag);
    if (accountId) searchParams.set('accountId', accountId);
    return this.fetch(`/api/actions/tag?${searchParams}`, { method: 'POST' });
  }

  async markAsRead(emailId: string): Promise<{ success: boolean }> {
    return this.fetch(`/api/actions/mark-read?emailId=${emailId}`, { method: 'POST' });
  }

  // Sync endpoint
  async syncEmails(accountId?: string): Promise<{ success: boolean; emailsFetched: number }> {
    return this.fetch('/api/sync', {
      method: 'POST',
      body: JSON.stringify({ accountId }),
    });
  }
}

export const api = new ApiClient();
